// Safe retries for PO creation: one request ID per intentional create, reused until the outcome is known.
const KEY_TTL_MS = 24 * 60 * 60 * 1000;
const storageKey = (tenantId, userId) => `po_create_request_${tenantId}_${userId}`;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const newRequestId = () => (
  window.crypto?.randomUUID ? window.crypto.randomUUID() : `po-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
);

export const getPendingRequestId = (tenantId, userId) => {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey(tenantId, userId)) || 'null');
    if (saved?.id && Date.now() - saved.savedAt < KEY_TTL_MS) return saved.id;
    const id = newRequestId();
    localStorage.setItem(storageKey(tenantId, userId), JSON.stringify({ id, savedAt: Date.now() }));
    return id;
  } catch {
    return newRequestId();
  }
};

export const clearPendingRequestId = (tenantId, userId) => {
  try { localStorage.removeItem(storageKey(tenantId, userId)); } catch { /* noop */ }
};

// No response, timeout, or gateway error: the server may or may not have saved the PO.
export const isUncertainError = (error) => (
  !error?.response || error.code === 'ECONNABORTED' || [502, 503, 504].includes(error.response.status)
);

export const idempotencyMismatch = (error) => {
  const detail = error?.response?.status === 409 ? error.response.data?.detail : null;
  return detail?.code === 'idempotency_key_mismatch' ? detail : null;
};

// Re-sends the SAME request (same request ID) while the outcome is uncertain; the server replays the original PO.
export async function sendWithRecovery(send, { delays = [2000, 4000, 8000], onChecking } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= delays.length; attempt += 1) {
    if (attempt > 0) {
      onChecking?.(attempt);
      await sleep(delays[attempt - 1]);
    }
    try {
      return await send();
    } catch (error) {
      if (!isUncertainError(error)) throw error;
      lastError = error;
    }
  }
  lastError.uncertain = true;
  throw lastError;
}
